/**
 * Reopening a decided finding or suggestion: a confirmation that shows the
 * decision about to be cleared — verdict, version, note, proof, who and when —
 * and says what reopening does and does not do. A dialog, like rejecting, so a
 * decision of record is never cleared by a stray click.
 */
import { RotateCcwIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { formatDate } from "./PolicyGovernanceTabs";
import type { ReopenSummary } from "./ReopenDialog.logic";

export function ReopenDialog({
  open,
  onOpenChange,
  title,
  summary,
  onReopen,
  children,
  confirmDisabled = false,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The item's own title, so the dialog says what it is reopening. */
  readonly title: string | null;
  /** The decision being cleared; null renders nothing. */
  readonly summary: ReopenSummary | null;
  readonly onReopen: () => Promise<string | null>;
  /** Extra fields under the decision, e.g. a required reason (findings redesign demo seam). */
  readonly children?: ReactNode;
  /** Holds the confirm button until the extra fields are complete. */
  readonly confirmDisabled?: boolean;
}) {
  const submitting = useRef(false);
  // After a reopen the Reopen button that opened this is gone with the
  // decision; the caller moves focus to the next decision instead, so the
  // dialog must not send it back to a detached trigger. A cancel still does.
  const reopened = useRef(false);
  useEffect(() => {
    if (open) reopened.current = false;
  }, [open]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (summary === null) return null;

  const submit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const message = await onReopen();
      if (message !== null) {
        setError(message);
        return;
      }
      reopened.current = true;
      onOpenChange(false);
    } catch {
      setError(`The ${summary.what} could not be reopened. Try again.`);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting.current) return;
        if (!next) setError(null);
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-lg" finalFocus={() => !reopened.current}>
        <DialogHeader>
          <DialogTitle>{`Reopen this ${summary.what}?`}</DialogTitle>
          <DialogDescription>
            {title ? `"${title}" — ` : ""}
            {summary.consequence}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3" aria-busy={busy}>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-xl border border-border/60 bg-muted/20 px-4 py-3 text-sm">
            {summary.facts.map((fact) => (
              <div key={fact.label} className="contents">
                <dt className="text-xs leading-5 text-muted-foreground">{fact.label}</dt>
                <dd className="min-w-0 whitespace-pre-line break-words leading-5 text-foreground">
                  {fact.value}
                </dd>
              </div>
            ))}
            {summary.decidedAt ? (
              <div className="contents">
                <dt className="text-xs leading-5 text-muted-foreground">When</dt>
                <dd className="leading-5 text-foreground">{formatDate(summary.decidedAt)}</dd>
              </div>
            ) : null}
          </dl>
          {children}
          {error !== null && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </DialogPanel>
        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" disabled={busy} className="cursor-pointer">
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void submit()}
            disabled={busy || confirmDisabled}
            aria-busy={busy}
            className="cursor-pointer gap-1.5"
          >
            <RotateCcwIcon aria-hidden className="size-3.5" />
            {busy ? "Reopening…" : `Reopen ${summary.what}`}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
